/**
 * Pure target picker for `alt+↑/↓` turn stepping. Kept free of the DOM so the
 * "which turn am I on" rules can be unit-tested — the hook only measures
 * offsets and scrolls.
 */

/**
 * How far the turn we last jumped to may drift from the top and still count as
 * where the reader is. Landing a jump reflows nearby rows, which nudges the
 * anchor off the top edge by a few dozen pixels; without this slack the next
 * step would re-select the turn we are already on.
 */
export const RESUME_TOLERANCE_PX = 96;

/**
 * Pick the next turn index to step to.
 *
 * `offsets[i]` is that turn's top edge relative to the scroll container's top
 * (negative = already scrolled past). `lastTargetIndex` is the turn the previous
 * step landed on, or `-1` when the reader has scrolled by hand since.
 * `viewportHeight` is the scroll container's client height — used to tell
 * "first turn is on screen under session chrome" from "first turn is below
 * the fold".
 *
 * Returns an index into `offsets`, or `-1` when there is nowhere to go.
 */
export function pickTurnStepTargetIndex({
  offsets,
  direction,
  lastTargetIndex,
  viewportHeight,
}: {
  offsets: readonly number[];
  direction: -1 | 1;
  lastTargetIndex: number;
  viewportHeight: number;
}): number {
  if (offsets.length === 0) return -1;

  const resumeOk =
    lastTargetIndex >= 0 &&
    lastTargetIndex < offsets.length &&
    Math.abs(offsets[lastTargetIndex]) <= RESUME_TOLERANCE_PX;

  // Last turn whose top has reached or passed the viewport top. Session chrome
  // (setup card, status) often sits above turn 1, so at scrollTop=0 that
  // anchor is still well below the edge — do not use a padding band here, or
  // mid-thread turns near the top get the same treatment.
  const passed = lastIndexWhere(offsets, (top) => top <= 0);
  let current = resumeOk ? lastTargetIndex : passed;

  // At the top of the transcript nothing has passed the edge, but turn 1 is
  // already on screen under the chrome and the rail has it highlighted. Count
  // it as current so the first down-press advances to turn 2 instead of
  // re-selecting turn 1 (a no-op the reader has to pay for twice).
  if (!resumeOk && current < 0 && offsets[0] < viewportHeight) {
    current = 0;
  }

  if (direction === -1) {
    if (current > 0) return current - 1;
    return -1;
  }

  if (current >= 0 && current < offsets.length - 1) return current + 1;
  // First turn is still below the fold: snap to it rather than nowhere.
  if (current < 0) return 0;
  return -1;
}

function lastIndexWhere(values: readonly number[], predicate: (value: number) => boolean): number {
  for (let i = values.length - 1; i >= 0; i--) {
    if (predicate(values[i])) return i;
  }
  return -1;
}
